import React, { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { Container, Box, Button, Stack, Typography, TextField, FormHelperText, Stepper, Step, StepLabel, CircularProgress } from '@mui/material';
import styled from '@emotion/styled';
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

import { currencyFormatter } from '../helpers';
import { db } from '../firebase';
import AdminNavbar from '../components/AdminNavbar';
import CheckoutForm from '../components/CheckoutForm';

const schema = yup.object({
  recipientName: yup.string().required().label('Recipient Name'),
  recipientEmail: yup.string().email().required().label('Recipient Email'),
  yourName: yup.string().required().label('Your Name'),
}).required();

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const ImageContainerStyled = styled.div`
    position: relative;

    .card-image {
        width: 100%;
    }

    .gift-card-value {
        position: absolute;
        background-color: rgba(0,0,0,0.7);
        padding: 5px 15px;
        font-weight: bold;
        font-size: 24px;
        color: #fff;
        line-height: 45px;
        left: 20px;
        bottom: 55px;
        border-radius: 20px;
    }
`;

export default function GiftCard() {
  const [activeStep, setActiveStep] = useState(1);
  const [cardOptions, setCardOptions] = useState(null);
  const [cardValue, setCardValue] = useState(null);
  const [stripeOptions, setStripeOptions] = useState({});

  useEffect(() => {
    return onSnapshot(doc(db, 'settings', 'giftCards'), (snap) => {
      const amounts = snap.data()?.amounts;
      if (Array.isArray(amounts) && amounts.length) {
        setCardOptions(amounts);
        setCardValue(amounts[0]);
      }
    });
  }, []);

  const { control, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      recipientName: '',
      recipientEmail: '',
      yourName: '',
      message: '',
    },
    resolver: yupResolver(schema)
  });

  const steps = [
    {
      label: 'Details'
    },
    {
      label: 'Payment',
    }
  ];

  const initPayment = async (data) => {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    }

    try {
      const res = await fetch(process.env.REACT_APP_FN_URL + "/createGiftCardPaymentIntent", requestOptions);
      const result = await res.json();
      console.log(result);
      setStripeOptions(result)
    } catch (error) {
      console.error(error);
    }
  }

  const onSubmit = data => {
    initPayment({ ...data, amount: cardValue });
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  }

  const handleSelectCardValue = (option) => {
    setCardValue(option);
  };

  return (
    <div>
      <AdminNavbar />
      <Container component="main" maxWidth="xs" sx={{
        marginTop: '16px',
        marginBottom: '16px',
      }}>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          {steps.map((step) => {
            return (
              <Step key={step?.label}>
                <StepLabel>{step?.label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>
        {
          activeStep === 1 && !cardOptions && (
            <Stack alignItems="center" sx={{ py: 8 }}>
              <CircularProgress />
            </Stack>
          )
        }
        {
          activeStep === 1 && cardOptions && (
            <div>
              <ImageContainerStyled className="card-container">
                <img className="card-image" src={process.env.PUBLIC_URL + '/giftcard-example.png'} alt="gift-card" />
                <Box className="gift-card-value">
                  {currencyFormatter(cardValue)}
                </Box>
              </ImageContainerStyled>
              <Box className="card-options">
                <Typography variant="h6" component="h3" sx={{
                  textTransform: 'uppercase',
                  marginBottom: '16px',
                }}>choose an amount</Typography>
                <Stack direction="row" gap={2} sx={{
                  flexWrap: 'wrap',
                  marginRight: '-16px'
                }}>
                  {
                    cardOptions.map((option) => (
                      <Button key={option} onClick={() => handleSelectCardValue(option)} variant="outlined" color={option === cardValue ? 'primary' : 'success'} sx={{
                        maxWidth: 'calc(100% * (1 / 4) - 16px)',
                        width: '100%',
                      }}>{currencyFormatter(option)}</Button>
                    ))
                  }
                </Stack>
              </Box>
              <Box className="gift-card-form">
                <Typography variant="h6" component="h3" sx={{
                  textTransform: 'uppercase',
                  marginTop: '16px',
                  marginBottom: '32px',
                }}>Add a personalized message.</Typography>
                <div className='form-group'>
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <Stack spacing={2}>
                      <Controller
                        name="recipientName"
                        control={control}
                        render={({ field }) => <TextField type="text" label="Recipient Name" variant="outlined" {...field} />}
                      />
                      {
                        errors?.recipientName && (
                          <FormHelperText error>{errors.recipientName?.message}</FormHelperText>
                        )
                      }
                      <Controller
                        name="recipientEmail"
                        control={control}
                        render={({ field }) => <TextField type="email" label="Recipient Email" variant="outlined" {...field} />}
                      />
                      {
                        errors?.recipientEmail && (
                          <FormHelperText error>{errors.recipientEmail?.message}</FormHelperText>
                        )
                      }
                      <Controller
                        name="yourName"
                        control={control}
                        render={({ field }) => <TextField type="text" label="Your Name" variant="outlined" {...field} />}
                      />
                      {
                        errors?.yourName && (
                          <FormHelperText error>{errors.yourName?.message}</FormHelperText>
                        )
                      }
                      <Controller
                        name="message"
                        control={control}
                        render={({ field }) => <TextField
                          id="outlined-multiline-flexible"
                          label="Message"
                          multiline
                          maxRows={4}
                          {...field}
                        />}
                      />
                      <Button type="submit" variant="contained">Purchase</Button>
                    </Stack>
                  </form>

                </div>
              </Box>
            </div>
          )
        }
        {
          activeStep === 2 && stripeOptions?.clientSecret && (
            <>
              <Elements stripe={stripePromise} options={stripeOptions}>
                <CheckoutForm paymentIntentId={stripeOptions.id} amount={cardValue} />
              </Elements>
            </>
          )
        }
      </Container>
    </div>
  );
};
